import { formatGold } from '../format';
import { nodeLevel } from '../gameApi';
import type { ScreenMount } from '../types';
import { buildDamageRecap } from './damageRecap';
import { icon } from '../icons';

/**
 * Écran de **run perdue** — COMMENTS v0.4.1 §1.3.
 *
 * Le prestige a disparu (§1.2) : la défaite acquittée (`acknowledgeRunLost()`)
 * repart directement en Accalmie, jour 1, niveau 1. L'écran dit donc exactement
 * ce qui change de main :
 *  - **perdus** : l'or, les dés Maîtres équipés et ceux de l'inventaire ;
 *  - **conservé** : l'arbre de talents (et le Gobelet, qui en découle) ;
 *  - avec le talent **Conservation** (`arc_keep`), le dé Maître de
 *    l'emplacement 1 survit aussi.
 */

/**
 * Nœuds *Conservation* — Arcanes. `arc_keep` garde l'emplacement 1 (A5) ;
 * v0.7.0 (COMMENTS v0.6.0 §1.1) ajoute *Conservation II* (`arc_keep2`) et
 * *Conservation III* (`arc_keep3`) pour les emplacements 2 et 3. Les
 * emplacements 4 et 5 ne sont jamais conservés (DECISIONS v0.7.0).
 *
 * La liste est parcourue dans l'ordre : un nœud absent de l'arbre du cœur
 * n'apporte simplement aucun emplacement, sans casser la phrase.
 */
const KEEP_NODES: { id: string; slot: number }[] = [
  { id: 'arc_keep', slot: 1 },
  { id: 'arc_keep2', slot: 2 },
  { id: 'arc_keep3', slot: 3 },
  // v0.26.0 (COMMENTS v0.25.0 §1.2) : Conservation IV et V.
  { id: 'arc_keep4', slot: 4 },
  { id: 'arc_keep5', slot: 5 },
];

/** Chiffres romains des niveaux de *Conservation* (II à V). */
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V'];

/** « l’emplacement 1 » / « les emplacements 1 et 2 » / « … 1, 2 et 3 ». */
function keptSlotsText(slots: number[]): string {
  if (slots.length === 1) return `le dé Maître de l’emplacement ${slots[0]}`;
  const head = slots.slice(0, -1).join(', ');
  return `les dés Maîtres des emplacements ${head} et ${slots[slots.length - 1]}`;
}

export const mountRunLost: ScreenMount = (container, ctx) => {
  const { game } = ctx;
  const summary = game.state.lastRun;
  // Abandon (A30) : même écran, titre dédié — `lastRun.abandoned` optionnel
  // tant que le cœur ne le publie pas encore.
  const abandoned = summary?.abandoned === true;
  // `lastRun.day` est le jour **atteint** (6 = Invasion, MVP_DESIGN §1 / D7) :
  // l'afficher tel quel sous le libellé « Jours joués » donnait « 6 » pour une
  // run de 5 jours (QA).
  // Q15 (réponse GD du 12/09/2026) : « l'or affiché en fin de partie est l'or
  // obtenu lors de l'expédition » — donc l'or crédité pendant la run qui vient
  // de se terminer (`lastRun.goldEarned`), pas le cumul de la partie.
  const dayReached = summary?.day ?? 1;
  const dayLabel = dayReached >= 6 ? 'Invasion' : `${dayReached}/5`;
  const keptSlots = KEEP_NODES.filter((n) => nodeLevel(game, n.id) >= 1).map((n) => n.slot);
  const keeps = keptSlots.length > 0;
  // « Conservation » seule, ou « Conservation II / III » quand les talents
  // suivants sont possédés : le joueur doit savoir ce qu'il garde vraiment.
  const keepLabel = `Conservation${keptSlots.length > 1 ? ` ${ROMAN[Math.min(keptSlots.length, 5)]}` : ''}`;

  const el = document.createElement('div');
  el.className = 'screen screen--end screen--lost screen--end-recap';
  // v0.16.0 (refonte F1, lot 3) : bandeau de sang, tuiles de statistiques,
  // bilan « perdus / conservé » en panneau facetté avec icônes.
  el.innerHTML = `
    <div class="end-center">
      <header class="f-banner f-banner--loss">
        <h1 class="end-title">${abandoned ? 'Run abandonnée' : 'Le village est tombé…'}</h1>
      </header>
      <dl class="end-stats">
        <div class="f-stat end-stat"><dt>Niveau atteint</dt><dd class="num">${summary?.level ?? 1}</dd></div>
        <div class="f-stat end-stat"><dt>Étape atteinte</dt><dd class="num">${dayLabel}</dd></div>
        <div class="f-stat f-stat--gold end-stat"><dt>Or gagné (run)</dt><dd class="num">${formatGold(summary?.goldEarned ?? 0)}</dd></div>
        <div class="f-stat end-stat"><dt>Runs</dt><dd class="num">${summary?.runs ?? 0}</dd></div>
      </dl>
      <div class="f-panel end-keep" data-role="run-keep">
        <p class="end-keep-line end-keep-line--lost">${icon('close')}<span><strong>Perdus :</strong> l'or, les dés Maîtres équipés et ceux de l'inventaire.</span></p>
        <p class="end-keep-line end-keep-line--kept">${icon('shield')}<span><strong>Conservé :</strong> l'arbre de talents, et le Gobelet qui en découle.</span></p>
        ${
          keeps
            ? `<p class="end-keep-line end-keep-node" data-role="keep-node">${icon('check')}<span>${keepLabel} : ${keptSlotsText(
                keptSlots,
              )} vous ${keptSlots.length > 1 ? 'suivent' : 'suit'} dans la prochaine run.</span></p>`
            : ''
        }
      </div>
    </div>
    <button type="button" class="f-btn f-btn--primary f-btn--block end-action" data-action="new-run">Nouvelle run</button>
  `;
  // Récapitulatif des dégâts (COMMENTS §2) — le combat perdu est justement
  // celui que le joueur veut analyser.
  el.insertBefore(buildDamageRecap(game, { open: false }), el.lastElementChild);
  container.appendChild(el);

  const btn = el.querySelector<HTMLButtonElement>('[data-action="new-run"]');
  const onClick = (): void => game.acknowledgeRunLost();
  btn?.addEventListener('click', onClick);

  return () => btn?.removeEventListener('click', onClick);
};
